import { BinLookupBackend, type BinLookupServiceConfig } from '@anchorbase/pillar-sdk';
import { useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { useUnsavedChangesRegistration } from '@/lib/hooks/useUnsavedChangesGuard';
import { runInBackground } from '@/lib/utils';
import { BinLookupApiConfigEditor } from './binlookupapi/BinLookupApiConfigEditor';
import { ValorBinLookupConfigEditor } from './valor/ValorBinLookupConfigEditor';

export function BinLookupServiceConfigurationCard(props: {
    readonly organizationUuid: string;
    readonly config: BinLookupServiceConfig | null | undefined;
    readonly canEdit: boolean;
    readonly disabled: boolean;
    readonly isSaving: boolean;
    readonly saveError: string | null;
    readonly onSave: (config: BinLookupServiceConfig | null) => Promise<void>;
}) {
    const [isEditing, setIsEditing] = useState(false);
    const [backend, setBackend] = useState<BinLookupBackend | null>(null);
    const [valorCredentialUuid, setValorCredentialUuid] = useState('');
    const [validationError, setValidationError] = useState<string | null>(null);
    useUnsavedChangesRegistration(isEditing);

    const beginEditing = (): void => {
        setBackend(props.config?.bin_lookup_backend ?? null);
        setValorCredentialUuid(
            props.config?.bin_lookup_backend === BinLookupBackend.VALOR
                ? props.config.credential_uuid
                : ''
        );
        setValidationError(null);
        setIsEditing(true);
    };
    const save = async (): Promise<void> => {
        let config: BinLookupServiceConfig | null;
        switch (backend) {
            case null:
                config = null;
                break;
            case BinLookupBackend.VALOR:
                if (valorCredentialUuid === '') {
                    setValidationError('Select a Valor virtual terminal credential.');
                    return;
                }
                config = {
                    bin_lookup_backend: BinLookupBackend.VALOR,
                    credential_uuid: valorCredentialUuid,
                };
                break;
            case BinLookupBackend.BINLOOKUPAPI:
                config = { bin_lookup_backend: BinLookupBackend.BINLOOKUPAPI };
                break;
        }
        try {
            await props.onSave(config);
            setIsEditing(false);
        } catch {
            // The parent exposes the mutation error through saveError.
        }
    };

    return (
        <Card id="bin-lookup-service-card" className="mb-6">
            <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>BIN Lookup Service</CardTitle>
                {props.canEdit && !isEditing && (
                    <Button size="sm" onClick={beginEditing} disabled={props.disabled}>
                        Edit
                    </Button>
                )}
            </CardHeader>
            <CardContent className="space-y-4">
                {isEditing ? (
                    <>
                        <div className="space-y-2">
                            <Label htmlFor="bin-lookup-backend">Backend</Label>
                            <Select
                                value={backend ?? 'NONE'}
                                onValueChange={(value) => {
                                    switch (value) {
                                        case 'NONE':
                                            setBackend(null);
                                            setValorCredentialUuid('');
                                            return;
                                        case BinLookupBackend.VALOR:
                                            setBackend(BinLookupBackend.VALOR);
                                            return;
                                        case BinLookupBackend.BINLOOKUPAPI:
                                            setBackend(BinLookupBackend.BINLOOKUPAPI);
                                            setValorCredentialUuid('');
                                            return;
                                    }
                                }}
                            >
                                <SelectTrigger id="bin-lookup-backend">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="NONE">Not configured</SelectItem>
                                    <SelectItem value={BinLookupBackend.VALOR}>Valor</SelectItem>
                                    <SelectItem value={BinLookupBackend.BINLOOKUPAPI}>
                                        binlookupapi
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {backend === BinLookupBackend.VALOR && (
                            <ValorBinLookupConfigEditor
                                organizationUuid={props.organizationUuid}
                                selectedCredentialUuid={valorCredentialUuid}
                                onSelectedCredentialUuidChange={setValorCredentialUuid}
                            />
                        )}
                        {backend === BinLookupBackend.BINLOOKUPAPI && <BinLookupApiConfigEditor />}
                        {(validationError ?? props.saveError) !== null && (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {validationError ?? props.saveError}
                                </AlertDescription>
                            </Alert>
                        )}
                        <div className="flex gap-2">
                            <Button
                                size="sm"
                                onClick={() => runInBackground(save(), () => undefined)}
                                disabled={props.isSaving}
                            >
                                {props.isSaving ? 'Saving...' : 'Save'}
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={props.isSaving}
                                onClick={() => setIsEditing(false)}
                            >
                                Cancel
                            </Button>
                        </div>
                    </>
                ) : props.config === undefined || props.config === null ? (
                    <p className="text-sm text-muted-foreground">
                        No BIN lookup backend configured.
                    </p>
                ) : (
                    <div className="space-y-1 text-sm">
                        <div>
                            <span className="text-muted-foreground">Backend:</span>{' '}
                            {props.config.bin_lookup_backend === BinLookupBackend.BINLOOKUPAPI
                                ? 'binlookupapi'
                                : 'Valor'}
                        </div>
                        {props.config.bin_lookup_backend === BinLookupBackend.VALOR && (
                            <div>
                                <span className="text-muted-foreground">Credential:</span>{' '}
                                {props.config.credential_uuid}
                            </div>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
